import { httpsCallable } from '../firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { recordAttempt, recordFailure } from '../telemetry/deliveryFailures';
import { isTelemetryDeliveryEnabled, sendWhenAuthed } from '../telemetry/authGate';

let currentUserId: string | undefined;
let currentUserEmail: string | undefined;
let sessionId: string = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export function setUsageTrackingUser(userId?: string, email?: string) {
  currentUserId = userId;
  currentUserEmail = email;
}

export function trackPageView(route: string) {
  trackEvent('page_view', { route });
}

/**
 * Records one feature use. ONLY `feature` and `action` are stored (the server keeps an explicit field allowlist, #4863): anything
 * passed in `metadata` is dropped without an error, so do not rely on it being persisted.
 */
export function trackFeatureUsage(feature: string, action: string, metadata?: Record<string, unknown>) {
  trackEvent('feature_usage', { feature, action, ...metadata });
}

function trackEvent(type: string, data: Record<string, unknown>) {
  // Only track in production. Read through the helper, never inline: see
  // isTelemetryDeliveryEnabled for why an inline NODE_ENV check is untestable here.
  if (!isTelemetryDeliveryEnabled()) return;

  // Captured NOW, not at send time: `trackPageView` fires on the first route
  // render, which is before auth resolves, so the event can sit in the gate's
  // buffer for a moment. Its route and timestamp must describe when it
  // happened, not when it was released.
  const occurred = { type, ...data, sessionId, timestamp: Date.now() };

  // `logUsageEvent` is a withOrgAuth callable, so calling it before Firebase
  // auth resolves returns 401 — which is what every page load used to do. The
  // gate holds the event until auth is known, then sends it, or drops and
  // COUNTS it if the visitor turns out to be signed out.
  sendWhenAuthed(() => {
    const logUsageEvent = httpsCallable(functions, 'logUsageEvent');
    recordAttempt();
    logUsageEvent({
      ...occurred,
      // Identity is read at SEND time, and these fields are INERT either way.
      //
      // An earlier comment here claimed setUsageTrackingUser has already run by
      // the time a buffered event flushes. That is FALSE: bindTelemetryAuthGate
      // subscribes at module load, before AuthProvider mounts, so the flush is
      // driven by onAuthStateChanged and routinely precedes the profile load
      // that calls setUsageTrackingUser — a flushed page view will often carry
      // undefined here. It does not matter, which is the only reason this is a
      // comment fix and not a bug fix: logUsageEvent overwrites userId/userEmail
      // from context.auth server-side, so these are display noise, never
      // authority. Do not build anything on them.
      userId: currentUserId,
      userEmail: currentUserEmail,
    }).catch(recordFailure);
  });
  // Still fire-and-forget: analytics must never block or break a page. What
  // changed (#4022) is that the drop is now COUNTABLE. This was
  // `.catch(() => {})`, so a failing telemetry endpoint was indistinguishable
  // from "nobody used the feature" -- the one time it was noticed, it was only
  // because a different error channel happened to catch it. recordFailure never
  // throws, so the non-blocking contract is unchanged.
}
